import { useEffect, useRef, useState } from 'react';

import { bind } from 'bind-event-listener';

interface UseDwellTrackerConfig {
	elementRef: React.RefObject<HTMLElement>;
	onDwellEnd?: (dwellTime: number) => void;
}

export const useDwellTracker = ({ elementRef, onDwellEnd }: UseDwellTrackerConfig): void => {
	const [isInViewport, setIsInViewport] = useState(false);
	const [tabHasFocus, setTabHasFocus] = useState(true);
	const dwellStartTimeRef = useRef<number>();
	const isDwellingRef = useRef(false);

	// Track viewport intersection
	useEffect(() => {
		if (!elementRef.current) {
			return;
		}

		const observer = new IntersectionObserver(
			([entry]) => {
				setIsInViewport(entry.isIntersecting);
			},
			{ threshold: 1.0 },
		);

		observer.observe(elementRef.current);

		return () => {
			observer.disconnect();
		};
		// eslint-disable-next-line react-hooks/exhaustive-deps
	}, [elementRef.current]);

	// Set up focus and navigation event listeners
	useEffect(() => {
		const handleVisibilityChange = () => {
			setTabHasFocus(!document.hidden);
		};

		const handleFocus = () => setTabHasFocus(true);
		const handleBlur = () => setTabHasFocus(false);

		const handleNavigation = () => {
			if (isDwellingRef.current && dwellStartTimeRef.current) {
				const dwellTime = Date.now() - dwellStartTimeRef.current;
				onDwellEnd?.(dwellTime);
			}
		};

		// Set up all event listeners using bind-event-listener
		const unbindVisibilityChange = bind(document, {
			type: 'visibilitychange',
			listener: handleVisibilityChange,
		});

		const unbindFocus = bind(window, {
			type: 'focus',
			listener: handleFocus,
		});

		const unbindBlur = bind(window, {
			type: 'blur',
			listener: handleBlur,
		});

		const unbindNavigation = bind(window, {
			type: 'beforeunload',
			listener: handleNavigation,
		});

		return () => {
			unbindVisibilityChange();
			unbindFocus();
			unbindBlur();
			unbindNavigation();
			// Fire on unmount if still dwelling
			if (isDwellingRef.current && dwellStartTimeRef.current) {
				const dwellTime = Date.now() - dwellStartTimeRef.current;
				onDwellEnd?.(dwellTime);
			}
		};
		// eslint-disable-next-line react-hooks/exhaustive-deps
	}, []);

	// Handle dwell start/end logic based on state changes
	useEffect(() => {
		const shouldDwell = isInViewport && tabHasFocus;

		// Start dwelling
		if (shouldDwell && !isDwellingRef.current) {
			dwellStartTimeRef.current = Date.now();
			isDwellingRef.current = true;
		}

		// End dwelling (viewport exit)
		if (!shouldDwell && isDwellingRef.current && dwellStartTimeRef.current) {
			const dwellTime = Date.now() - dwellStartTimeRef.current;
			onDwellEnd?.(dwellTime);
			isDwellingRef.current = false;
			dwellStartTimeRef.current = undefined;
		}
		// eslint-disable-next-line react-hooks/exhaustive-deps
	}, [isInViewport, tabHasFocus]);
};
